import axios from "axios";
import { msalInstance, state } from "./msalConfig";
import { jwtDecode } from "jwt-decode";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { clearAllCache } from "@/Services/CacheService";

const apiScope = window.env?.MSAL_API_SCOPE;
if (!apiScope) {
  throw new Error('window.env.MSAL_API_SCOPE must be defined in config.js');
}
const apiScopes = [apiScope];
const graphScopes = ["User.Read"];

let interceptorRegistered = false;

export function msalService() {
  const login = async () => {
    try {
      clearAllCache()
      await msalInstance.loginRedirect({
        scopes: [...apiScopes, ...graphScopes],
      });
    } catch (error) {
      console.error("Login error:", error);
    }
  };

  const logout = () => {
    try {
      clearAllCache()
      msalInstance.logoutRedirect();
      state.isAuthenticated = false;
      state.user = null;
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleRedirect = async () => {
    try {
      const result = await msalInstance.handleRedirectPromise();
      if (result) {
        msalInstance.setActiveAccount(result.account);
      }

      const activeAccount = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
      if (activeAccount) {
        msalInstance.setActiveAccount(activeAccount);
        state.isAuthenticated = true;
        state.user = activeAccount;

        const token = await getToken(apiScopes);
        const decodedToken = jwtDecode(token);
        return decodedToken;
      } else {
        state.isAuthenticated = false;
        state.user = null;
      }
    } catch (error) {
      console.error("Redirect error:", error);
    }
  };

  const getToken = async (customScopes = apiScopes) => {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.warn("No active account. Redirecting to login...");
      await msalInstance.loginRedirect({ scopes: customScopes });
      return;
    }

    try {
      const silentResponse = await msalInstance.acquireTokenSilent({
        scopes: customScopes,
        account,
      });
      return silentResponse.accessToken;
    } catch (error) {
      console.error("Silent token acquisition failed:", error);

      if (error instanceof InteractionRequiredAuthError) {
        console.log("Redirecting to acquire token interactively...");
        await msalInstance.acquireTokenRedirect({ scopes: customScopes });
      } else {
        throw error;
      }
    }
  };

  const registerAuthorizationHeaderInterceptor = () => {
    if (interceptorRegistered) return;
    axios.interceptors.request.use(async (config) => {
      try {
        const accessToken = await getToken(apiScopes);
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch (tokenError) {
        console.error("Error adding Authorization header:", tokenError);
      }
      return config;
    });
    interceptorRegistered = true;
  };

  return {
    login,
    logout,
    handleRedirect,
    getToken,
    registerAuthorizationHeaderInterceptor,
    getTokenForGraph: () => getToken(graphScopes),
  };
}

export function getCurrentUserId() {
  const account = msalInstance.getActiveAccount();
  return account?.homeAccountId ?? null;
}